import React, { useState, useEffect } from 'react';
import Layout from 'components/ui/Layout';
import { Edit } from 'lucide-react';
import { usePrefetchConfig } from 'features/globalConfiguration/hooks/usePrefetchConfig';
import { useGlobalConfig } from 'features/globalConfiguration/hooks/useGlobalConfig';
import { EditGlobalModal } from 'features/globalConfiguration/components/editGlobalConfigModal';
import { useUserProfile } from 'features/auth/hooks/useUserProfile';



const GlobalConfiguration = () => {
    const { prefetchGlobalConfig } = usePrefetchConfig();
    const { data: globalConfigData, isLoading, refetch } = useGlobalConfig();

     const { data: profileData } = useUserProfile();
          const user = profileData?.data;
          
          const restrictedRoles = [
            "delivery_manager",
            "travel_manager",
            "operations_manager",
            "category_manager",
            "finance_manager",
          ];
          
          const showPermissionError = restrictedRoles.includes(
            user?.role?.name
          );


    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    useEffect(() => {
        prefetchGlobalConfig();
    }, [prefetchGlobalConfig]);

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
      <Layout>
        {showPermissionError ? (
          <div className="flex items-center justify-center min-h-[80vh]">
            <p className="text-destructive text-lg font-medium text-center">
              Error loading configuration: You do not have permission to perform
              this action!
            </p>
          </div>
        ) : (
          <div className="p-6 bg-background min-h-screen">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  Global Configuration
                </h1>
                <p className="text-sm text-muted-foreground">
                  Manage global system settings
                </p>
              </div>

              <button
                onClick={() => setIsEditModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                <Edit size={16} />
                Edit Configuration
              </button>
            </div>

            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-xl font-semibold text-foreground mb-6">
                Current Settings
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Service Radius
                  </h4>
                  <p className="text-foreground text-lg">
                    {globalConfigData?.data?.search_radius_in_meter / 1000} KM
                  </p>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Platform Fees
                  </h4>
                  <p className="text-foreground text-lg">
                    {globalConfigData?.data?.platform_fees}
                  </p>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    GST Percentage
                  </h4>
                  <p className="text-foreground text-lg">
                    {globalConfigData?.data?.gst_percentage}
                  </p>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Booking Commission (%)
                  </h4>
                  <p className="text-foreground text-lg">
                    {globalConfigData?.data?.booking_commission_percentage}
                  </p>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Commission Waiver Days
                  </h4>
                  <p className="text-foreground text-lg">
                    {globalConfigData?.data?.booking_commission_waiver_days}
                  </p>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    SOS Helpline Number
                  </h4>
                  <p className="text-foreground text-lg">
                    {globalConfigData?.data?.support_contact_number || "-"}
                  </p>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                    Contact Email
                  </h4>
                  <p className="text-foreground text-lg">
                    {globalConfigData?.data?.support_contact_email || "-"}
                  </p>
                </div>
              </div>
            </div>

            {isEditModalOpen && (
              <EditGlobalModal
                isOpen={isEditModalOpen}
                onClose={() => {
                  setIsEditModalOpen(false);
                  refetch();
                }}
                defaultValues={globalConfigData?.data}
              />
            )}
          </div>
        )}
      </Layout>
    );
}

export default GlobalConfiguration